import {
  API,
  DEVICE_API,
  type AuthResult,
  type DeviceConfig,
  type DeviceCreated,
  type DeviceSummary,
  type LeaderboardBoard,
  type LeaderboardPage,
  type LeaderboardScope,
  type PairingCreated,
  type PairingStatus,
  type PlayerProfile,
  type PlayerRanks,
  type PlayerStats,
  type PracticeProgress,
  type RegisterPlayerRequest,
  type ScoreState,
  type UpdateDeviceRequest,
  type UpdatePlayerRequest,
} from '@aiscoreboard/contracts';
import { withRequestTimeout } from './requestTimeout.ts';
import { controlToken } from './operator.ts';
import { clearSessionDeadline } from './sessionTimeout.ts';

const TOKEN_PREFIX = 'aiscoreboard.token.';

/**
 * Session tokens are held per seat.
 *
 * A venue tablet has two players sitting at it, so a single "current user"
 * would mean signing one of them out to sign the other in.
 */
export type Seat = 0 | 1;

export function storedToken(seat: Seat): string | null {
  return localStorage.getItem(TOKEN_PREFIX + seat);
}

export function storeToken(seat: Seat, token: string | null): void {
  if (token) localStorage.setItem(TOKEN_PREFIX + seat, token);
  else localStorage.removeItem(TOKEN_PREFIX + seat);
  clearSessionDeadline(TOKEN_PREFIX + seat);
}

async function json<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    let message = `${response.status} ${response.statusText}`;
    try {
      const parsed = JSON.parse(detail) as { error?: unknown };
      if (typeof parsed.error === 'string') message = parsed.error;
      else if (parsed.error) message = describeValidation(parsed.error);
    } catch {
      if (detail) message = sanitizeErrorDetail(detail, message);
    }
    throw new Error(message);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Proxy and gateway failures often come back as full HTML pages. */
function sanitizeErrorDetail(detail: string, fallback: string): string {
  const text = detail.trim();
  if (text === '') return fallback;
  // Avoid dumping a whole HTML error document into a red UI label.
  if (text.startsWith('<') || /<\/?(?:html|body|head|title|h1|p)\b/i.test(text)) {
    return fallback;
  }
  return text.length > 220 ? `${text.slice(0, 217)}...` : text;
}

/** Turn a Zod `flatten()` payload into something a player can act on. */
function describeValidation(error: unknown): string {
  const flat = error as { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
  const fields = Object.entries(flat.fieldErrors ?? {}).map(
    ([field, messages]) => `${field}: ${messages.join(', ')}`,
  );
  const all = [...(flat.formErrors ?? []), ...fields];
  return all.length ? all.join('; ') : 'invalid request';
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; token?: string | null; signal?: AbortSignal; cache?: RequestCache } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.token) headers.authorization = `Bearer ${options.token}`;

  return json<T>(
    await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.cache ? { cache: options.cache } : {}),
    }),
  );
}

export function registerPlayer(input: RegisterPlayerRequest): Promise<AuthResult> {
  return request(API.register, { method: 'POST', body: input });
}

export function login(username: string, password: string): Promise<AuthResult> {
  return request(API.login, { method: 'POST', body: { username, password } });
}

export function logout(token: string): Promise<void> {
  return withRequestTimeout(10_000, signal =>
    request<void>(API.logout, { method: 'POST', body: {}, token, signal }));
}

export function fetchMe(token: string): Promise<PlayerProfile> {
  return request(API.me, { token });
}

/** Open a pairing for a phone to claim. See `PairingCreated` for the two secrets. */
export function createPairing(): Promise<PairingCreated> {
  return request(API.pair, { method: 'POST', body: {} });
}

/** Polled by the scoreboard. Throws once the pairing has expired or been used. */
export function pairingStatus(pollSecret: string): Promise<PairingStatus> {
  return request(`${API.pairStatus}?secret=${encodeURIComponent(pollSecret)}`);
}

/** Called on the phone, once it has an account, to take the seat. */
export function claimPairing(token: string, code: string): Promise<void> {
  return request(API.claimPair, { method: 'POST', body: { code }, token });
}

export function updateMe(token: string, patch: UpdatePlayerRequest): Promise<PlayerProfile> {
  return request(API.updateMe, { method: 'PATCH', body: patch, token });
}

export function closeAccount(token: string, password: string, confirm: string): Promise<void> {
  return request(API.deleteMe, { method: 'DELETE', body: { password, confirm }, token });
}

/** Prove the password before the settings screen unlocks. */
export function confirmPassword(token: string, password: string): Promise<void> {
  return request(API.confirmPassword, { method: 'POST', body: { password }, token });
}

export function fetchMyRanks(token: string): Promise<PlayerRanks> {
  return request(API.myRanks, { token });
}

export async function uploadAvatar(
  token: string,
  file: File,
  password: string,
): Promise<PlayerProfile> {
  const body = new FormData();
  // Before the file, so the server has it parsed by the time the upload lands.
  body.append('password', password);
  body.append('avatar', file);
  return json(
    await fetch(API.avatar, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
      body,
    }),
  );
}

export interface StatsResponse {
  player: PlayerProfile;
  stats: PlayerStats;
  practice: PracticeProgress;
}

export function fetchMyStats(token: string): Promise<StatsResponse> {
  return request(API.myStats, { token });
}

export function searchPlayers(query: string): Promise<{ players: PlayerProfile[] }> {
  return request(`${API.playerSearch}?q=${encodeURIComponent(query)}`);
}

export function fetchLeaderboard(
  board: LeaderboardBoard,
  scope: LeaderboardScope,
  token: string | null,
  place: { country?: string; city?: string } = {},
): Promise<LeaderboardPage> {
  const params = new URLSearchParams({ board, scope });
  if (place.country) params.set('country', place.country);
  if (place.city) params.set('city', place.city);
  return request(`${API.leaderboard}?${params.toString()}`, { token });
}

export function startPractice(token: string): Promise<{ id: string }> {
  return request(API.practiceSessions, { method: 'POST', body: { mode: 'free' }, token });
}

export function finishPractice(
  token: string,
  id: string,
  result: { pointsScored: number; ballsPotted: number; highestBreak: number; breaks: number[] },
): Promise<{ progress: PracticeProgress }> {
  return request(API.finishPractice(id), { method: 'POST', body: result, token });
}

// ── Device / camera management ────────────────────────────────────────────

export function listDevices(token: string): Promise<{ devices: DeviceSummary[] }> {
  return request(DEVICE_API.devices, { token });
}

export function createDevice(
  token: string,
  name: string,
  venueId?: string,
): Promise<DeviceCreated> {
  return request(DEVICE_API.devices, { method: 'POST', body: { name, ...(venueId ? { venueId } : {}) }, token });
}

export function updateDevice(
  token: string,
  id: string,
  patch: UpdateDeviceRequest,
): Promise<DeviceSummary> {
  return request(DEVICE_API.device(id), { method: 'PATCH', body: patch, token });
}

export function rotateDeviceToken(token: string, id: string): Promise<{ token: string }> {
  return request(DEVICE_API.rotateToken(id), { method: 'POST', token });
}

export function deleteDevice(token: string, id: string): Promise<void> {
  return request(DEVICE_API.device(id), { method: 'DELETE', token });
}

// ── Device self-management (authenticated by device token, no user login) ──

export function getDeviceConfig(deviceToken: string): Promise<DeviceConfig> {
  return request(DEVICE_API.config, { token: deviceToken });
}

export function updateDeviceConfig(
  deviceToken: string,
  patch: UpdateDeviceRequest,
): Promise<DeviceConfig> {
  return request(DEVICE_API.selfUpdate, { method: 'PATCH', body: patch, token: deviceToken });
}

export function getDeviceMatches(
  deviceToken: string,
): Promise<{ matches: Array<{ id: string; state: ScoreState }> }> {
  return request(DEVICE_API.selfMatches, { token: deviceToken });
}

// ── Authenticated camera catalog and administrator settings ───────────────

export function listActiveDevices(signal?: AbortSignal): Promise<{ devices: DeviceSummary[] }> {
  return request(DEVICE_API.active, { token: controlToken(), ...(signal ? { signal } : {}), cache: 'no-store' });
}

export function listCameraDevices(signal?: AbortSignal, token = controlToken()): Promise<{ devices: DeviceSummary[] }> {
  return request(DEVICE_API.catalog, { token, ...(signal ? { signal } : {}), cache: 'no-store' });
}

export interface CameraAccess {
  platformAdmin: boolean;
  venues: Array<{ id: string; name: string | null; role: string | null; canConfigure: boolean }>;
}
export function cameraAccess(token: string): Promise<CameraAccess> {
  return request('/api/cameras/access', { token, cache: 'no-store' });
}

export function updateActiveDevice(
  id: string,
  patch: {
    matchId?: string | null;
    streaming?: boolean;
    rtmpUrl?: string | null;
    streamKey?: string | null;
  },
  token = controlToken(),
): Promise<DeviceSummary> {
  return request(DEVICE_API.activeDevice(id), { method: 'PATCH', body: patch, token });
}

export function listPublicMatches(): Promise<{ matches: Array<{ id: string; state: ScoreState }> }> {
  return request(DEVICE_API.selfMatches);
}
